/**
 * userLearningStore — learner learning & purchases slice (Zustand).
 *
 *   GET  /api/learners/courses/enrolled            GET /api/learners/library
 *   GET  /api/learners/courses/:courseId/learn     GET /api/learners/purchases
 *   GET  /api/learners/purchases/:reference        GET /api/learners/purchases/:reference/verify
 *   POST /api/learners/courses/:id/purchase
 *   POST /api/learners/books/:id/purchase
 *   POST /api/learners/journals/:id/purchase
 *   POST /api/payments/initialize          GET /api/payments/:reference/verify
 */
import { apiRequest, normalizeList, downloadFile } from "./userUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const userLearningInitialState = {
  enrolledCourses: [],
  enrolledCoursesLoading: false,
  enrolledCoursesError: null,

  learnView: null,
  learnViewLoading: false,
  learnViewError: null,

  userLibrary: [],
  userLibraryLoading: false,
  userLibraryError: null,
  userLibraryTotal: 0,
  userLibraryPages: 1,
  myLibraryResources: [],
  myLibraryCourses: [],
  myLibraryLoading: false,
  myLibraryError: null,

  userPurchases: [],
  userPurchasesLoading: false,
  userPurchasesError: null,
  userPurchasesTotal: 0,
  userPurchasesPages: 1,

  userPurchaseDetail: null,
  userPurchaseDetailLoading: false,
  userPurchaseDetailError: null,

  userPurchaseMutationLoading: false,
  userPurchaseMutationError: null,

  // Payments — POST initialize, GET verify
  paymentInitResult: null,
  paymentInitLoading: false,
  paymentInitError: null,

  paymentVerifyResult: null,
  paymentVerifyLoading: false,
  paymentVerifyError: null,

  // Course progress — GET continue, GET lesson, POST lesson complete
  continueCourse: null,
  continueCourseLoading: false,
  continueCourseError: null,

  courseLesson: null,
  courseLessonLoading: false,
  courseLessonError: null,

  quizAttempts: [],
  quizAttemptsLoading: false,
  quizAttemptsError: null,
  quizAttemptsTotal: 0,
  quizAttemptsPages: 1,

  quizAttemptResult: null,
  quizAttemptSubmitLoading: false,
  quizAttemptSubmitError: null,

  lessonSubmission: null,
  lessonSubmissionLoading: false,
  lessonSubmissionError: null,
  lessonSubmissionSubmitLoading: false,
  lessonSubmissionSubmitError: null,

  lessonCompleteLoading: false,
  lessonCompleteError: null,

  // Library item detail + download
  libraryItem: null,
  libraryItemLoading: false,
  libraryItemError: null,
  libraryDownloadLoading: false,
  libraryDownloadError: null,
};

export function createUserLearningActions(set, get) {
  async function fetchList(prefix, path, params = {}, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const query = new URLSearchParams(params).toString();
      const data = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
      const { items, total, totalPages } = normalizeList(data, keys);
      set({
        [`${prefix}Loading`]: false,
        [prefix]: items,
        [`${prefix}Total`]: total,
        [`${prefix}Pages`]: totalPages,
      });
      return { success: true, items, total, totalPages };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  async function purchase(path, body = {}) {
    set({ userPurchaseMutationLoading: true, userPurchaseMutationError: null });
    try {
      const data = await apiRequest(get, path, { method: "POST", body });
      set({
        userPurchaseMutationLoading: false,
        userPurchaseMutationError: null,
        successMessage: "Purchase initiated",
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const msg = toDisplayString(err.message, "Failed to initiate purchase");
      set({ userPurchaseMutationLoading: false, userPurchaseMutationError: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // GET /api/learners/my-library — completed purchases and course enrollments.
    fetchMyLibrary: async () => {
      set({ myLibraryLoading: true, myLibraryError: null });
      try {
        const data = await apiRequest(get, "/api/learners/my-library");
        const payload = data?.data ?? data;
        const resources = Array.isArray(payload?.resources) ? payload.resources : [];
        const courses = Array.isArray(payload?.courses) ? payload.courses : [];
        set({ myLibraryResources: resources, myLibraryCourses: courses, myLibraryLoading: false });
        return { success: true, resources, courses };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load your library");
        set({ myLibraryLoading: false, myLibraryError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/my-library/:itemType/:itemId/download
    downloadMyLibraryResource: async (itemType, itemId) => {
      try {
        const result = await apiRequest(
          get,
          `/api/learners/my-library/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}/download`
        );
        return { success: true, data: result?.data ?? result };
      } catch (err) {
        return { success: false, error: toDisplayString(err.message, "Download failed") };
      }
    },

    // POST /api/learners/cart/items — stage a selected item for one cart order.
    addUserCartItem: async (itemType, itemId) => {
      try {
        const data = await apiRequest(get, "/api/learners/cart/items", {
          method: "POST",
          body: {itemType, itemId},
        });
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        return { success: false, error: toDisplayString(err.message, "Could not add this item to checkout") };
      }
    },

    // POST /api/learners/cart/checkout — one verified order for the cart.
    checkoutUserCart: async () => {
      try {
        const data = await apiRequest(get, "/api/learners/cart/checkout", {method: "POST"});
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        return { success: false, error: toDisplayString(err.message, "Could not start cart checkout") };
      }
    },

    // GET /api/learners/courses/enrolled
    fetchEnrolledCourses: (params = {}) =>
      fetchList("enrolledCourses", "/api/learners/courses/enrolled", params, ["courses"]),

    // GET /api/learners/courses/:courseId/learn
    fetchLearnView: async (courseId, params = {}) => {
      set({ learnViewLoading: true, learnViewError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/learners/courses/${courseId}/learn${query ? `?${query}` : ""}`
        );
        const view = data?.data ?? data;
        set({ learnViewLoading: false, learnView: view });
        return { success: true, data: view };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load course");
        set({ learnViewLoading: false, learnViewError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/library
    fetchUserLibrary: (params = {}) =>
      fetchList("userLibrary", "/api/learners/library", params, ["items", "books", "journals"]),

    // GET /api/learners/purchases
    fetchUserPurchases: (params = {}) =>
      fetchList("userPurchases", "/api/learners/purchases", params, ["purchases"]),

    // GET /api/learners/purchases/:reference
    fetchUserPurchase: async (reference) => {
      set({ userPurchaseDetailLoading: true, userPurchaseDetailError: null });
      try {
        const data = await apiRequest(get, `/api/learners/purchases/${reference}`);
        const detail = data?.data ?? data?.purchase ?? data;
        set({ userPurchaseDetailLoading: false, userPurchaseDetail: detail });
        return { success: true, data: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch purchase");
        set({ userPurchaseDetailLoading: false, userPurchaseDetailError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/purchases/:reference/verify
    verifyUserPurchase: async (reference) => {
      set({ userPurchaseDetailLoading: true, userPurchaseDetailError: null });
      try {
        const data = await apiRequest(get, `/api/learners/purchases/${reference}/verify`);
        const detail = data?.data ?? data?.purchase ?? data;
        set({ userPurchaseDetailLoading: false, userPurchaseDetail: detail });
        return { success: true, data: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to verify purchase");
        set({ userPurchaseDetailLoading: false, userPurchaseDetailError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/learners/courses/:id/purchase
    purchaseUserCourse: (id, body = {}) => purchase(`/api/learners/courses/${id}/purchase`, body),
    // POST /api/learners/books/:id/purchase
    purchaseUserBook: (id, body = {}) => purchase(`/api/learners/books/${id}/purchase`, body),
    // POST /api/learners/journals/:id/purchase
    purchaseUserJournal: (id, body = {}) => purchase(`/api/learners/journals/${id}/purchase`, body),

    // POST /api/payments/initialize — { transactionType, itemId }
    initializeUserPayment: async (body = {}) => {
      set({ paymentInitLoading: true, paymentInitError: null });
      try {
        const data = await apiRequest(get, "/api/payments/initialize", {
          method: "POST",
          body,
        });
        const result = data?.data ?? data;
        set({ paymentInitLoading: false, paymentInitResult: result });
        return { success: true, data: result };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to start payment");
        set({ paymentInitLoading: false, paymentInitError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/payments/:reference/verify
    verifyUserPayment: async (reference) => {
      set({ paymentVerifyLoading: true, paymentVerifyError: null });
      try {
        const data = await apiRequest(get, `/api/payments/${reference}/verify`);
        const result = data?.data ?? data;
        set({ paymentVerifyLoading: false, paymentVerifyResult: result });
        return { success: true, data: result };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to verify payment");
        set({ paymentVerifyLoading: false, paymentVerifyError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/courses/:courseId/continue — next lesson to continue
    fetchContinueCourse: async (courseId) => {
      set({ continueCourseLoading: true, continueCourseError: null });
      try {
        const data = await apiRequest(get, `/api/learners/courses/${courseId}/continue`);
        const view = data?.data ?? data;
        set({ continueCourseLoading: false, continueCourse: view });
        return { success: true, data: view };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load next lesson");
        set({ continueCourseLoading: false, continueCourseError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/courses/:courseId/lessons/:lessonId
    fetchCourseLesson: async (courseId, lessonId) => {
      set({ courseLessonLoading: true, courseLessonError: null });
      try {
        const data = await apiRequest(
          get,
          `/api/learners/courses/${courseId}/lessons/${lessonId}`
        );
        const lesson = data?.data ?? data?.lesson ?? data;
        set({ courseLessonLoading: false, courseLesson: lesson });
        return { success: true, data: lesson };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load lesson");
        set({ courseLessonLoading: false, courseLessonError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/courses/:courseId/lessons/:lessonId/quiz/attempts
    fetchUserQuizAttempts: async (courseId, lessonId, params = {}) => {
      set({ quizAttemptsLoading: true, quizAttemptsError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const path = `/api/learners/courses/${courseId}/lessons/${lessonId}/quiz/attempts`;
        const response = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
        const { items, total, totalPages } = normalizeList(response, [
          "attempts",
          "quizAttempts",
          "items",
        ]);
        set({
          quizAttemptsLoading: false,
          quizAttempts: items,
          quizAttemptsTotal: total,
          quizAttemptsPages: totalPages,
        });
        return { success: true, items, total, totalPages, data: response?.data ?? response };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load quiz attempts");
        set({ quizAttemptsLoading: false, quizAttemptsError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/learners/courses/:courseId/lessons/:lessonId/quiz/attempts
    submitUserQuizAttempt: async (courseId, lessonId, body) => {
      set({ quizAttemptSubmitLoading: true, quizAttemptSubmitError: null });
      try {
        const response = await apiRequest(
          get,
          `/api/learners/courses/${courseId}/lessons/${lessonId}/quiz/attempts`,
          { method: "POST", body }
        );
        const result = response?.data ?? response;
        set({ quizAttemptSubmitLoading: false, quizAttemptResult: result });
        return { success: true, data: result };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to submit quiz attempt");
        set({ quizAttemptSubmitLoading: false, quizAttemptSubmitError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/courses/:courseId/lessons/:lessonId/submission
    fetchUserLessonSubmission: async (courseId, lessonId) => {
      set({ lessonSubmissionLoading: true, lessonSubmissionError: null });
      try {
        const response = await apiRequest(
          get,
          `/api/learners/courses/${courseId}/lessons/${lessonId}/submission`
        );
        const submission = response?.data ?? response?.submission ?? response;
        set({ lessonSubmissionLoading: false, lessonSubmission: submission });
        return { success: true, data: submission };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load lesson submission");
        set({ lessonSubmissionLoading: false, lessonSubmissionError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/learners/courses/:courseId/lessons/:lessonId/submission
    submitUserLessonSubmission: async (courseId, lessonId, body) => {
      set({ lessonSubmissionSubmitLoading: true, lessonSubmissionSubmitError: null });
      try {
        const response = await apiRequest(
          get,
          `/api/learners/courses/${courseId}/lessons/${lessonId}/submission`,
          { method: "POST", body }
        );
        const submission = response?.data ?? response?.submission ?? response;
        set({
          lessonSubmissionSubmitLoading: false,
          lessonSubmission: submission,
        });
        return { success: true, data: submission };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to submit lesson work");
        set({ lessonSubmissionSubmitLoading: false, lessonSubmissionSubmitError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/learners/courses/:courseId/lessons/:lessonId/complete
    completeLesson: async (courseId, lessonId) => {
      set({ lessonCompleteLoading: true, lessonCompleteError: null });
      try {
        const data = await apiRequest(
          get,
          `/api/learners/courses/${courseId}/lessons/${lessonId}/complete`,
          { method: "POST", body: {} }
        );
        set({
          lessonCompleteLoading: false,
          lessonCompleteError: null,
          successMessage: "Lesson completed",
        });
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to complete lesson");
        set({ lessonCompleteLoading: false, lessonCompleteError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/library/books/:id
    fetchLibraryBook: async (id) => {
      set({ libraryItemLoading: true, libraryItemError: null });
      try {
        const data = await apiRequest(get, `/api/learners/library/books/${id}`);
        const item = data?.data ?? data?.book ?? data;
        set({ libraryItemLoading: false, libraryItem: item });
        return { success: true, data: item };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load book");
        set({ libraryItemLoading: false, libraryItemError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/library/books/:id/download
    downloadLibraryBook: async (id, filename) => {
      set({ libraryDownloadLoading: true, libraryDownloadError: null });
      try {
        const result = await downloadFile(
          get,
          `/api/learners/library/books/${id}/download`,
          filename
        );
        set({ libraryDownloadLoading: false });
        return { success: true, ...result };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to download book");
        set({ libraryDownloadLoading: false, libraryDownloadError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/library/journals/:id
    fetchLibraryJournal: async (id) => {
      set({ libraryItemLoading: true, libraryItemError: null });
      try {
        const data = await apiRequest(get, `/api/learners/library/journals/${id}`);
        const item = data?.data ?? data?.journal ?? data;
        set({ libraryItemLoading: false, libraryItem: item });
        return { success: true, data: item };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load journal");
        set({ libraryItemLoading: false, libraryItemError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/library/journals/:id/download
    downloadLibraryJournal: async (id, filename) => {
      set({ libraryDownloadLoading: true, libraryDownloadError: null });
      try {
        const result = await downloadFile(
          get,
          `/api/learners/library/journals/${id}/download`,
          filename
        );
        set({ libraryDownloadLoading: false });
        return { success: true, ...result };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to download journal");
        set({ libraryDownloadLoading: false, libraryDownloadError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const userLearningActions = createUserLearningActions;
export default userLearningActions;
